import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { IoStar } from 'react-icons/io5';
import { TbMessageCircle } from 'react-icons/tb';

const COLORS = {
  primary: '#2196f3',
  success: '#4caf50',
  warning: '#ffc107',
  error: '#f44336',
  purple: '#673ab7',
  background: '#f7f9fc',
  lightBorder: '#e3f2fd',
};

const SURVEY_TYPES = {
  rating: {
    icon: IoStar,
    label: 'Calificación',
  },
  text: {
    icon: TbMessageCircle,
    label: 'Respuesta de texto',
  },
};

const RATING_SCALES = [
  { value: 3, label: '1 a 3 estrellas' },
  { value: 5, label: '1 a 5 estrellas' },
  { value: 10, label: '1 a 10 estrellas' },
];

const PreviewCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: '#f0f8ff',
  border: `1px solid ${COLORS.lightBorder}`,
  borderRadius: theme.shape.borderRadius,
  marginTop: theme.spacing(2),
}));

const WhatsAppPreview = styled(Box)(({ theme }) => ({
  backgroundColor: '#25D366',
  color: 'white',
  padding: theme.spacing(2),
  borderRadius: '18px 18px 18px 4px',
  maxWidth: '80%',
  marginBottom: theme.spacing(1),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: -8,
    width: 0,
    height: 0,
    border: '8px solid transparent',
    borderTopColor: '#25D366',
    borderBottom: 0,
    borderRight: 0,
  },
}));

const UserPreview = styled(Box)(({ theme }) => ({
  backgroundColor: '#E8E8E8',
  color: '#000',
  padding: theme.spacing(2),
  borderRadius: '18px 18px 4px 18px',
  maxWidth: '80%',
  marginLeft: 'auto',
  marginBottom: theme.spacing(1),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    right: -8,
    width: 0,
    height: 0,
    border: '8px solid transparent',
    borderTopColor: '#E8E8E8',
    borderBottom: 0,
    borderLeft: 0,
  },
}));

const getTypeText = type => {
  return SURVEY_TYPES[type]?.label || 'Texto';
};

const SurveyPreview = ({ data }) => {
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
        Vista previa de la encuesta
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Mensaje del bot */}
        <WhatsAppPreview>
          <Typography variant="body1">{data.question}</Typography>
        </WhatsAppPreview>

        {/* Respuesta del usuario */}
        <UserPreview>
          {data.type === 'rating' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">{data.max_rating || 5}</Typography>
            </Box>
          ) : (
            <Typography variant="body2">
              ("Respuesta ingresada por el usuario")
            </Typography>
          )}
        </UserPreview>
      </Box>

      {/* Tipo de encuesta */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: 'white', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Tipo:</strong> {getTypeText(data.type)}
        </Typography>
        {data.type === 'rating' && (
          <Typography variant="body2" color="text.secondary">
            <strong>Escala:</strong> 1 a {data.max_rating || 5} estrellas
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const SurveyTypeSelector = ({ formData, setFormData }) => {
  const handleTypeChange = newType => {
    if (newType === 'rating') {
      setFormData({
        ...formData,
        type: newType,
        max_rating: formData.max_rating || 5,
      });
    } else {
      setFormData({ ...formData, type: newType, max_rating: null });
    }
  };

  return (
    <FormControl fullWidth sx={{ mb: 3 }}>
      <InputLabel>Tipo de encuesta</InputLabel>
      <Select
        value={formData.type}
        label="Tipo de encuesta"
        onChange={e => handleTypeChange(e.target.value)}>
        {Object.entries(SURVEY_TYPES).map(([value, config]) => {
          const Icon = config.icon;
          return (
            <MenuItem key={value} value={value}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icon />
                {config.label}
              </Box>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

const RatingScaleSelector = ({ formData, setFormData }) => {
  if (formData.type !== 'rating') return null;

  return (
    <FormControl fullWidth sx={{ mb: 3 }}>
      <InputLabel>Escala de calificación</InputLabel>
      <Select
        value={formData.max_rating || 5}
        label="Escala de calificación"
        onChange={e =>
          setFormData({ ...formData, max_rating: e.target.value })
        }>
        {RATING_SCALES.map(scale => (
          <MenuItem key={scale.value} value={scale.value}>
            {scale.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const FormFields = ({ formData, setFormData }) => {
  return (
    <Box sx={{ pt: 2 }}>
      {/* Título de la encuesta */}
      <TextField
        fullWidth
        label="Título de la encuesta"
        variant="outlined"
        value={formData.title}
        onChange={e => setFormData({ ...formData, title: e.target.value })}
        sx={{ mb: 3 }}
      />

      {/* Pregunta del chatbot */}
      <TextField
        fullWidth
        label="Pregunta que enviará el chatbot"
        variant="outlined"
        multiline
        rows={3}
        value={formData.question}
        onChange={e => setFormData({ ...formData, question: e.target.value })}
        sx={{ mb: 3 }}
        placeholder="Ej: ¿Cómo calificarías tu experiencia con nuestro chatbot?"
      />

      {/* Selector de tipo */}
      <SurveyTypeSelector formData={formData} setFormData={setFormData} />

      {/* Selector de escala (solo para rating) */}
      <RatingScaleSelector formData={formData} setFormData={setFormData} />
    </Box>
  );
};

const DialogActionsSection = ({
  onClose,
  onSave,
  formData,
  loading,
  editingSurvey,
}) => {
  const isFormValid = formData.title && formData.question;

  return (
    <DialogActions>
      <Button onClick={onClose} disabled={loading}>
        Cancelar
      </Button>
      <Button
        variant="contained"
        onClick={onSave}
        disabled={!isFormValid || loading}>
        {loading ? 'Guardando...' : editingSurvey ? 'Actualizar' : 'Crear'}
      </Button>
    </DialogActions>
  );
};

const SurveyForm = ({
  open,
  onClose,
  onSave,
  editingSurvey,
  formData,
  setFormData,
  loading = false,
}) => {
  const shouldShowPreview = formData.title && formData.question;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editingSurvey ? 'Editar Encuesta' : 'Nueva Encuesta de Satisfacción'}
      </DialogTitle>

      <DialogContent>
        <FormFields formData={formData} setFormData={setFormData} />

        {/* Vista previa */}
        {shouldShowPreview && (
          <PreviewCard>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Vista previa
            </Typography>
            <SurveyPreview data={formData} />
          </PreviewCard>
        )}
      </DialogContent>

      <DialogActionsSection
        onClose={onClose}
        onSave={onSave}
        formData={formData}
        loading={loading}
        editingSurvey={editingSurvey}
      />
    </Dialog>
  );
};

export default SurveyForm;
